import { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner, Badge, Card, Row, Col, InputGroup } from 'react-bootstrap';
import { useApi } from '../../../contexts/ApiProvider';
import ProjectMetricsChart from './ProjectMetricsChart';
import { getOptimizedUrl } from '../../../utils/imageUtils';

export default function ProjectLogsModal({ show, onHide, project, blueprint, items }) {
    const api = useApi();
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);

    const getLocalISOString = (dateInput) => {
        const d = dateInput ? parseSafeDate(dateInput) : new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16);
    };

    const [formData, setFormData] = useState({
        log_type: 'General Update',
        timestamp: getLocalISOString(),
        notes: '',
        metrics: {},
        image: null
    });

    useEffect(() => {
        if (show && project) {
            fetchLogs();
            setFormData(prev => ({ ...prev, timestamp: getLocalISOString() }));
        }
        // eslint-disable-next-line
    }, [show, project]);

    const parseSafeDate = (rawDate) => {
        if (!rawDate) return new Date();
        if (typeof rawDate !== 'string') return new Date(rawDate);

        let safeString = rawDate;
        if (safeString.includes(' ') && !safeString.includes('T')) {
            safeString = safeString.replace(' ', 'T');
        }
        if (!safeString.endsWith('Z')) {
            safeString = safeString.split('.')[0] + 'Z';
        }
        return new Date(safeString);
    };

    const formatToLocal = (rawDateString) => {
        if (!rawDateString) return '--';
        return parseSafeDate(rawDateString).toLocaleString([], {
            dateStyle: 'medium',
            timeStyle: 'short'
        });
    };

    const fetchLogs = async () => {
        setIsLoading(true);
        const res = await api.get(`/hobby-projects/${project.id}/logs?per_page=100`);
        if (res.ok) setLogs(res.body.items || []);
        setIsLoading(false);
    };

    const handleDynamicChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            metrics: { ...prev.metrics, [fieldName]: value }
        }));
    };

    const openCreateForm = () => {
        setEditId(null);
        setFormData({ log_type: 'General Update', timestamp: getLocalISOString(), notes: '', metrics: {}, image: null });
        setShowForm(true);
    };

    const openEditForm = (log) => {
        setEditId(log.id);
        const formattedMetrics = { ...(log.metrics || {}) };
        blueprint.forEach(field => {
            if (field.data_type === 'datetime' && formattedMetrics[field.field_name]) {
                formattedMetrics[field.field_name] = getLocalISOString(formattedMetrics[field.field_name]);
            }
        });

        setFormData({
            log_type: log.log_type,
            timestamp: getLocalISOString(log.timestamp),
            notes: log.notes || '',
            metrics: formattedMetrics,
            image: null
        });

        setShowForm(true);
        document.querySelector('.modal-body').scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payloadMetrics = { ...formData.metrics };

        blueprint.forEach(field => {
            if (field.data_type === 'datetime' && payloadMetrics[field.field_name]) {
                payloadMetrics[field.field_name] = new Date(payloadMetrics[field.field_name]).toISOString();
            }
        });

        const payload = {
            log_type: formData.log_type,
            timestamp: new Date(formData.timestamp).toISOString(),
            notes: formData.notes,
            metrics: payloadMetrics
        };

        const logRes = editId
            ? await api.put(`/activity-logs/${editId}`, payload)
            : await api.post(`/hobby-projects/${project.id}/logs`, payload);

        if (logRes.ok) {
            if (formData.image) {
                const imageForm = new FormData();
                imageForm.append('image', formData.image);
                imageForm.append('log_id', editId || logRes.body.id);
                await api.post('/hobby-images', imageForm);
            }
            setShowForm(false);
            fetchLogs();
        } else {
            alert("Failed to save log.");
        }
    };

    const handleDelete = async (logId) => {
        if (!window.confirm("Delete this log permanently?")) return;
        const res = await api.delete(`/activity-logs/${logId}`);
        if (res.ok) fetchLogs();
    };

    const renderMetricValue = (fieldName, rawValue) => {
        if (!rawValue && rawValue !== 0 && typeof rawValue !== 'boolean') return '--';
        const fieldDef = blueprint.find(f => f.field_name === fieldName);

        if (fieldDef && fieldDef.data_type === 'item_reference') {
            const linkedItem = items.find(i => i.id.toString() === rawValue.toString());
            return linkedItem ? <span className="text-primary fw-bold">{linkedItem.name}</span> : <span className="text-danger">Item Not Found</span>;
        }

        if (fieldDef && fieldDef.data_type === 'multi_item_reference') {
            if (!Array.isArray(rawValue)) return rawValue.toString();
            const itemNames = rawValue.map(id => {
                const linkedItem = items.find(i => i.id.toString() === id.toString());
                return linkedItem ? linkedItem.name : 'Unknown';
            });
            return <span className="text-primary fw-bold">{itemNames.join(', ')}</span>;
        }

        if (fieldDef && fieldDef.data_type === 'datetime') {
            return formatToLocal(rawValue);
        }

        if (fieldDef && fieldDef.data_type === 'number') {
            const unit = (fieldDef.options && fieldDef.options.length > 0) ? ` ${fieldDef.options[0]}` : '';
            return `${rawValue}${unit}`;
        }

        if (fieldDef && fieldDef.data_type === 'boolean') {
            return rawValue ? 'Yes' : 'No';
        }

        return rawValue.toString();
    };

    if (!project) return null;

    return (
        <Modal show={show} onHide={onHide} centered size="lg" fullscreen="md-down" scrollable>
            <Modal.Header closeButton className="border-0 pb-0 bg-light">
                <Modal.Title className="fw-bold">
                    <span className="text-muted fs-6 d-block mb-1">Project Timeline</span>
                    {project.title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light p-4">

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="fw-bold text-uppercase letter-spacing-1 text-muted mb-0">Activity History</h6>
                    <Button variant={showForm ? "secondary" : "primary"} size="sm" className="rounded-pill fw-bold" onClick={() => showForm ? setShowForm(false) : openCreateForm()}>
                        <i className={`bi ${showForm ? 'bi-x-lg' : 'bi-plus-lg'} me-1`}></i> {showForm ? 'Cancel' : 'Add Log'}
                    </Button>
                </div>

                {showForm && (
                    <Card className="border-0 shadow-sm rounded-4 mb-4 border-top border-primary border-4">
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <div className="d-flex gap-3 mb-3">
                                    <Form.Group className="flex-grow-1">
                                        <Form.Label className="small fw-bold text-muted mb-1">Log Type / Phase</Form.Label>
                                        <Form.Control type="text" placeholder="e.g., Sensory Test, Repotting..." value={formData.log_type} onChange={e => setFormData({...formData, log_type: e.target.value})} required />
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold text-muted mb-1">Date & Time</Form.Label>
                                        <Form.Control type="datetime-local" value={formData.timestamp} onChange={e => setFormData({...formData, timestamp: e.target.value})} required />
                                    </Form.Group>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">Notes</Form.Label>
                                    <Form.Control as="textarea" rows={2} placeholder="What happened?" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
                                </Form.Group>

                                {blueprint.length > 0 && <div className="small fw-bold text-primary mb-2 mt-4 border-bottom pb-1">Custom Metrics</div>}
                                <div className="row g-3 mb-4">
                                    {blueprint.filter(field => {
                                        if (!field.target_category) return true;
                                        const currentLogType = (formData.log_type || '').trim().toLowerCase();
                                        const targetCategories = field.target_category.split(',').map(c => c.trim().toLowerCase());
                                        return targetCategories.includes(currentLogType);
                                    }).map(field => (
                                        <Col md={6} key={field.id}>
                                            <Form.Group className="fade-in">
                                                <Form.Label className="small fw-bold text-muted mb-1">{field.field_name}</Form.Label>

                                                {field.data_type === 'text' && <Form.Control size="sm" type="text" value={formData.metrics[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)} />}

                                                {field.data_type === 'number' && (
                                                    <InputGroup size="sm">
                                                        <Form.Control type="number" step="0.1" value={formData.metrics[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)} />
                                                        {field.options && field.options.length > 0 && (
                                                            <InputGroup.Text className="bg-light text-muted small">{field.options[0]}</InputGroup.Text>
                                                        )}
                                                    </InputGroup>
                                                )}

                                                {field.data_type === 'datetime' && <Form.Control size="sm" type="datetime-local" value={formData.metrics[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)} />}

                                                {field.data_type === 'boolean' && <Form.Check type="switch" checked={formData.metrics[field.field_name] || false} onChange={e => handleDynamicChange(field.field_name, e.target.checked)} />}

                                                {field.data_type === 'dropdown' && (
                                                    <Form.Select size="sm" value={formData.metrics[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)}>
                                                        <option value="">Select...</option>
                                                        {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                    </Form.Select>
                                                )}

                                                {field.data_type === 'item_reference' && (() => {
                                                    let availableItems = items;
                                                    if (field.options && field.options.length > 0) {
                                                        availableItems = items.filter(item => item.category && field.options.includes(item.category.trim()));
                                                    }
                                                    return (
                                                        <Form.Select size="sm" value={formData.metrics[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)}>
                                                            <option value="">Select item...</option>
                                                            {availableItems.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                                                        </Form.Select>
                                                    );
                                                })()}

                                                {field.data_type === 'multi_item_reference' && (() => {
                                                    let availableItems = items;
                                                    if (field.options && field.options.length > 0) {
                                                        availableItems = items.filter(item => item.category && field.options.includes(item.category.trim()));
                                                    }
                                                    return (
                                                        <>
                                                            <Form.Select size="sm" multiple value={formData.metrics[field.field_name] || []} onChange={e => {
                                                                const selectedOpts = Array.from(e.target.selectedOptions, option => option.value);
                                                                handleDynamicChange(field.field_name, selectedOpts);
                                                            }}>
                                                                {availableItems.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                                                            </Form.Select>
                                                            <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>Hold Ctrl/Cmd to select multiple.</Form.Text>
                                                        </>
                                                    );
                                                })()}
                                            </Form.Group>
                                        </Col>
                                    ))}
                                </div>

                                <Form.Group className="mb-4 pt-3 border-top">
                                    <Form.Label className="small fw-bold text-muted mb-1"><i className="bi bi-image me-1"></i>Attach Image (Optional)</Form.Label>
                                    <Form.Control type="file" accept="image/*" onChange={e => setFormData({...formData, image: e.target.files[0]})} />
                                </Form.Group>

                                <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill">Save Log</Button>
                            </Form>
                        </Card.Body>
                    </Card>
                )}

                {/* --- TIMELINE FEED --- */}
                {isLoading ? (
                    <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div>
                ) : logs.length === 0 ? (
                    <div className="text-center py-5 text-muted bg-white rounded-4 border">
                        <i className="bi bi-clock-history display-4 opacity-25 d-block mb-3"></i>
                        <p className="mb-0">No logs recorded yet.</p>
                    </div>
                ) : (
                    <>
                    <ProjectMetricsChart logs={logs} blueprint={blueprint} />
                    <div className="timeline-container ps-2 ms-2 border-start border-2 border-primary border-opacity-25">
                        {logs.map(log => (
                            <Card key={log.id} className="border-0 shadow-sm rounded-4 mb-4 ms-3 position-relative">
                                <div className="position-absolute bg-primary rounded-circle" style={{ width: '12px', height: '12px', left: '-23px', top: '24px' }}></div>
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <Badge bg="secondary" className="bg-opacity-10 text-dark border">{log.log_type}</Badge>
                                        <div className="small text-muted fw-bold">{formatToLocal(log.timestamp)}</div>
                                    </div>

                                    {log.notes && <p className="mb-3 text-dark">{log.notes}</p>}

                                    {log.metrics && Object.keys(log.metrics).length > 0 && (
                                        <div className="bg-light p-3 rounded-3 mb-3">
                                            <Row className="g-2">
                                                {Object.entries(log.metrics).map(([key, val]) => (
                                                    <Col sm={6} key={key}>
                                                        <div className="small">
                                                            <span className="text-muted fw-bold me-2">{key}:</span>
                                                            <span className="text-dark">{renderMetricValue(key, val)}</span>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </div>
                                    )}

                                    {log.images && log.images.length > 0 && (
                                        <div className="mb-3">
                                            {log.images.map((imgUrl, idx) => (
                                                <a key={idx} href={imgUrl} target="_blank" rel="noopener noreferrer" title="Click to view full resolution">
                                                    <img src={getOptimizedUrl(imgUrl, 400)} alt="Log attachment" className="img-fluid rounded-3 me-2 border shadow-sm hover-opacity-75 transition-all" style={{ maxHeight: '200px', cursor: 'zoom-in' }} />
                                                </a>
                                            ))}
                                        </div>
                                    )}

                                    <div className="text-end border-top pt-2 mt-2">
                                        <Button variant="link" className="text-primary p-0 small text-decoration-none me-3" onClick={() => openEditForm(log)}>
                                            <i className="bi bi-pencil-square me-1"></i>Edit
                                        </Button>
                                        <Button variant="link" className="text-danger p-0 small text-decoration-none" onClick={() => handleDelete(log.id)}>
                                            <i className="bi bi-trash3 me-1"></i>Delete
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}
